/* =============================================================
   StudentDiscounts.jsx — Student partner discounts
   -------------------------------------------------------------
   Lists exclusive discounts and deals negotiated by ESMT for
   exchange students: accommodation, insurance, travel, finance.
   Each card shows the brand, deal, and a "Get Discount" link.

   TODO (backend integration):
   - Replace static DISCOUNTS array with Supabase query
   ============================================================= */

import { useState } from 'react';
import StudentLayout          from '../../components/StudentLayout';
import WorkInProgress        from '../../components/WorkInProgress';
import { useStudentContext }  from '../../hooks/useStudentContext';

const DISCOUNTS = [
  {
    id: 1,
    brand: 'HousingAnywhere',
    category: 'Accommodation',
    icon: 'apartment',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    accent: 'border-violet-200',
    deal: '3 weeks free rent guarantee',
    description: 'Book your London student room on HousingAnywhere and get 3 weeks of rent protected for free if anything goes wrong. The go-to platform for international students finding housing abroad.',
    code: 'UNX-HA-LON',
    badge: 'Essential',
    badgeStyle: 'bg-violet-100 text-violet-700',
    link: 'housinganywhere.com',
  },
  {
    id: 2,
    brand: 'AXA Travel Insurance',
    category: 'Insurance',
    icon: 'health_and_safety',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    accent: 'border-emerald-200',
    deal: '15% off student exchange plans',
    description: 'Post-Brexit, the European EHIC card does NOT cover the UK. AXA offers exchange-specific plans covering medical, repatriation and liability — recommended for your stay in the UK.',
    code: 'UNX-AXA-LON',
    badge: null,
    badgeStyle: '',
    link: 'axa.co.uk',
  },
  {
    id: 3,
    brand: 'Wise',
    category: 'Finance',
    icon: 'currency_exchange',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    accent: 'border-green-200',
    deal: 'Free transfer up to £500',
    description: 'Send money from home in EUR and receive it in GBP with the real exchange rate and zero hidden fees. Perfect for paying rent and managing your monthly budget in London.',
    code: 'UNX-WISE-LON',
    badge: null,
    badgeStyle: '',
    link: 'wise.com',
  },
  {
    id: 4,
    brand: 'Revolut',
    category: 'Finance',
    icon: 'credit_card',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    accent: 'border-blue-200',
    deal: '3 months Premium free',
    description: 'Spend in GBP without currency conversion fees. Get a UK account number to receive money, split bills with flatmates and withdraw cash for free. Ideal for day-to-day life in London.',
    code: 'UNX-REV-LON',
    badge: 'Popular',
    badgeStyle: 'bg-blue-100 text-blue-700',
    link: 'revolut.com',
  },
  {
    id: 5,
    brand: 'Airalo',
    category: 'Connectivity',
    icon: 'sim_card',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    accent: 'border-orange-200',
    deal: '10% off UK eSIM plans',
    description: 'Activate a UK eSIM before you even land at Heathrow. No need to queue at the airport for a SIM card — get data and a UK number instantly on your phone.',
    code: 'UNX-AIR-LON',
    badge: null,
    badgeStyle: '',
    link: 'airalo.com',
  },
  {
    id: 6,
    brand: 'Eurostar',
    category: 'Travel',
    icon: 'train',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    accent: 'border-sky-200',
    deal: '10% off London ↔ Europe routes',
    description: 'London to Paris in 2h15, Brussels in 2h, Amsterdam in 4h. Make the most of your exchange by exploring Europe on weekends — Eurostar is the fastest way from London to the continent.',
    code: 'UNX-EURO-LON',
    badge: null,
    badgeStyle: '',
    link: 'eurostar.com',
  },
  {
    id: 7,
    brand: 'Omio',
    category: 'Travel',
    icon: 'directions_transit',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    accent: 'border-sky-200',
    deal: '€5 off your first booking',
    description: 'Compare and book trains, buses and flights across the UK and Europe in one place. Great for planning weekend trips from London to Edinburgh, Cardiff or anywhere in the UK.',
    code: 'UNX-OMIO-LON',
    badge: null,
    badgeStyle: '',
    link: 'omio.com',
  },
  {
    id: 8,
    brand: '16-25 Railcard',
    category: 'Travel',
    icon: 'confirmation_number',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    accent: 'border-amber-200',
    deal: '1/3 off all UK train tickets',
    description: 'The standard railcard for students in the UK — saves you 33% on every train journey across Britain. Buy it for £30/year and it pays for itself on the first long-distance trip.',
    code: 'UNX-RAIL-LON',
    badge: 'Essential',
    badgeStyle: 'bg-amber-100 text-amber-700',
    link: 'railcard.co.uk',
  },
  {
    id: 9,
    brand: 'Babbel',
    category: 'Language Courses',
    icon: 'translate',
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    accent: 'border-teal-200',
    deal: '50% off any subscription plan',
    description: 'Learn the local language before and during your exchange. Babbel offers short, practical lessons designed for real conversations — available in 14 languages including German, French, Spanish and Italian.',
    code: 'UNX-BAB-50',
    badge: 'Popular',
    badgeStyle: 'bg-teal-100 text-teal-700',
    link: 'babbel.com',
  },
  {
    id: 10,
    brand: 'Preply',
    category: 'Language Courses',
    icon: 'school',
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    accent: 'border-teal-200',
    deal: '20% off your first month of tutoring',
    description: 'Book 1-on-1 lessons with native-speaking tutors online. Perfect if you want to improve quickly before your exchange or need help with academic language at your host university.',
    code: 'UNX-PREP-20',
    badge: null,
    badgeStyle: '',
    link: 'preply.com',
  },
  {
    id: 11,
    brand: 'italki',
    category: 'Language Courses',
    icon: 'record_voice_over',
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    accent: 'border-teal-200',
    deal: '$10 credit on your first purchase',
    description: 'Connect with community tutors and professional teachers for affordable conversation practice. Great for maintaining your language skills or picking up the basics of your host country\'s language.',
    code: 'UNX-ITALKI',
    badge: null,
    badgeStyle: '',
    link: 'italki.com',
  },
];

const CATEGORIES = ['All', 'Accommodation', 'Insurance', 'Finance', 'Connectivity', 'Travel', 'Language Courses'];

export default function StudentDiscounts() {
  return (
    <StudentLayout>
      <WorkInProgress
        icon="local_offer"
        title="Discounts"
        description="Exclusive deals on accommodation, insurance, travel and more — negotiated for exchange students."
      />
    </StudentLayout>
  );

  // ── Real page below — remove the return above to restore it ──
  // eslint-disable-next-line no-unreachable
  const [activeCategory, setActiveCategory] = useState('All');
  const [copied, setCopied] = useState(null);

  const { student } = useStudentContext();
  // Incoming: they are AT the admin university → show universities.name
  // Outgoing: they are going abroad → show partner name or destination_university
  const isIncoming      = student?.type === 'incoming';
  const exchangeUni     = isIncoming
    ? (student?.universities?.name ?? null)
    : (student?.partner_universities?.name ?? student?.destination_university ?? null);
  const exchangeCountry = isIncoming
    ? (student?.universities?.country ?? null)
    : (student?.partner_universities?.country ?? null);

  const filtered = activeCategory === 'All'
    ? DISCOUNTS
    : DISCOUNTS.filter(d => d.category === activeCategory);

  const handleCopy = (id, code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <StudentLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">Student Discounts</h1>
        <p className="text-[#464555] mt-1 flex items-center gap-2">
          Exclusive deals for your exchange at
          {exchangeUni && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full">
              <span className="material-symbols-outlined text-[13px]">location_on</span>
              {exchangeUni}{exchangeCountry ? ` · ${exchangeCountry}` : ''}
            </span>
          )}
        </p>
      </div>

      {/* Category filters */}
      <div className="flex items-center gap-2 flex-wrap mb-8">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeCategory === cat
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600'
            }`}
          >
            {cat}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-400">{filtered.length} deals available</span>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-2 gap-5">
        {filtered.map(d => (
          <div
            key={d.id}
            className={`bg-white rounded-2xl shadow-sm border ${d.accent} overflow-hidden flex flex-col`}
          >
            {/* Card header */}
            <div className="px-5 pt-5 pb-4 flex items-start gap-4">
              <div className={`w-11 h-11 rounded-xl ${d.iconBg} flex items-center justify-center flex-shrink-0`}>
                <span className={`material-symbols-outlined text-[22px] ${d.iconColor}`}>{d.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{d.category}</span>
                  {d.badge && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${d.badgeStyle}`}>
                      {d.badge}
                    </span>
                  )}
                </div>
                <p className="font-display text-base font-semibold text-[#0b1c30] mt-0.5">{d.brand}</p>
              </div>
            </div>

            {/* Divider */}
            <div className="mx-5 border-t border-slate-100" />

            {/* Deal highlight */}
            <div className="px-5 py-3 bg-slate-50/60">
              <p className="text-sm font-semibold text-[#0b1c30] flex items-center gap-2">
                <span className="material-symbols-outlined text-[16px] text-indigo-500">sell</span>
                {d.deal}
              </p>
            </div>

            {/* Description */}
            <div className="px-5 py-4 flex-1">
              <p className="text-sm text-[#464555] leading-relaxed">{d.description}</p>
            </div>

            {/* Promo code + copy */}
            <div className="px-5 pb-5">
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                  <span className="material-symbols-outlined text-[15px] text-slate-400">tag</span>
                  <span className="font-mono text-sm font-bold text-[#0b1c30] tracking-widest flex-1">{d.code}</span>
                </div>
                <button
                  onClick={() => handleCopy(d.id, d.code)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-shrink-0 ${
                    copied === d.id
                      ? 'bg-emerald-500 text-white'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  <span className="material-symbols-outlined text-[16px]">
                    {copied === d.id ? 'check' : 'content_copy'}
                  </span>
                  {copied === d.id ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
                <span className="material-symbols-outlined text-[12px]">open_in_new</span>
                {d.link}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div className="mt-8 bg-indigo-50 border border-indigo-100 rounded-2xl p-5 flex gap-3">
        <span className="material-symbols-outlined text-indigo-500 text-[22px] flex-shrink-0 mt-0.5">info</span>
        <div>
          <p className="text-sm font-semibold text-indigo-800">These deals are curated by Unixplore</p>
          <p className="text-xs text-indigo-600 mt-1 leading-relaxed">
            Discounts are selected specifically for your destination and exchange period. If a code doesn't work or you need help, reach us at <a href="mailto:hello@unixplore.eu" className="underline">hello@unixplore.eu</a>.
          </p>
        </div>
      </div>
    </StudentLayout>
  );
}
